import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, AlertTriangle, ArrowLeft, CheckCircle, Home } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

// URL du logo officiel BabiDoc
const LOGO_URL = "https://customer-assets.emergentagent.com/job_babidoc-health/artifacts/7a0rukid_HUMMM0fc84adc-81cc-4751-9a5e-2c9327247823.png";

const PaymentRequired = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/create-checkout`, { origin_url: originUrl });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de l\'initialisation du paiement');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-green-50 flex items-center justify-center p-4" data-testid="payment-required-page">
      {/* Back to home button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors z-10"
        data-testid="back-home-btn"
      >
        <ArrowLeft className="w-6 h-6" />
        <Home className="w-5 h-5" />
        <span className="text-sm font-medium">Accueil</span>
      </button>

      <div className="max-w-lg w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img 
              src={LOGO_URL} 
              alt="BabiDoc" 
              className="w-16 h-16 object-cover rounded-full"
            />
            <h1 className="text-3xl font-bold text-teal-700">BabiDoc</h1>
          </div>
        </div>

        <Card className="shadow-xl border-amber-200">
          <CardHeader className="text-center bg-gradient-to-r from-amber-50 to-orange-50 rounded-t-lg">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
            </div>
            <CardTitle className="text-xl text-amber-900">
              Période d'essai terminée
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <p className="text-gray-600 mb-4">
                Bonjour <strong>{user?.full_name}</strong>,
              </p>
              <p className="text-gray-600 mb-4">
                Votre période d'essai gratuite d'un mois est maintenant terminée. 
                Pour continuer à être visible aux patients et recevoir des rendez-vous, 
                veuillez activer votre abonnement.
              </p>
            </div>

            {/* Pricing */}
            <div className="bg-gradient-to-r from-teal-50 to-green-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-teal-800 mb-4 text-center">
                Abonnement Mensuel
              </h3>
              <div className="text-center">
                <div className="text-4xl font-bold text-teal-700 mb-2">
                  20 000 <span className="text-xl">FCFA</span>
                </div>
                <p className="text-sm text-gray-500">par mois</p>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <span>Visibilité aux patients</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <span>Réception de rendez-vous</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <span>Gestion de votre profil</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <span>Support prioritaire</span>
                </div>
              </div>
            </div>

            {/* Payment Methods Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 text-center">
                <strong>Moyens de paiement acceptés:</strong><br />
                Carte bancaire (Visa, Mastercard)
              </p>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button 
                onClick={handleSubscribe} 
                className="w-full btn-primary h-12 text-base"
                disabled={loading}
                data-testid="subscribe-now-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                {loading ? 'Chargement...' : 'Payer maintenant'}
              </Button>
              
              <Button 
                onClick={handleLogout} 
                variant="outline"
                className="w-full h-12"
                data-testid="logout-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Se déconnecter
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              En vous abonnant, vous acceptez nos conditions d'utilisation. 
              Votre abonnement sera renouvelé automatiquement chaque mois.
            </p>
          </CardContent>
        </Card>

        {/* Contact Support */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Des questions? Contactez-nous à{' '}
            <a href="mailto:babidoc.net@gmail.com" className="text-teal-600 hover:underline">
              babidoc.net@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequired;
